import { parseJsonSafe } from "./json";
import { logger } from "./logger";

/**
 * Normalizes a geofence's polygon_data into a parsed GeoJSON object.
 * Accepts either an already-parsed object or a JSON string.
 * @param {Object|string} raw - The polygon_data value from a geofence
 * @returns {Object|null} Parsed GeoJSON object, or null if invalid
 */
export function parsePolygonData(raw) {
  if (!raw) return null;
  if (typeof raw === "string") {
    return parseJsonSafe(raw, null);
  }
  return raw;
}

/**
 * Converts a single geofence into an array of GeoJSON Features with geofence
 * metadata attached. Handles FeatureCollection, Feature, Polygon and
 * MultiPolygon formats. Always returns an array (possibly empty) for easy
 * concatenation/flattening by callers.
 * @param {Object} geofence - Geofence record containing polygon_data
 * @param {string} [status] - Status value to attach (defaults to geofence.status)
 * @returns {Array<Object>} Array of GeoJSON Features
 */
export function geofenceToFeatures(geofence, status = geofence?.status) {
  const polygonData = parsePolygonData(geofence?.polygon_data);
  if (!polygonData) return [];

  const properties = {
    geofence_id: geofence.id,
    geofence_name: geofence.name,
    geofence_status: status,
  };

  try {
    if (polygonData.type === "FeatureCollection") {
      return polygonData.features.map((feature) => ({
        ...feature,
        properties: { ...feature.properties, ...properties },
      }));
    }

    if (polygonData.type === "Feature") {
      return [
        {
          ...polygonData,
          properties: { ...polygonData.properties, ...properties },
        },
      ];
    }

    if (polygonData.type === "Polygon" || polygonData.type === "MultiPolygon") {
      return [
        {
          type: "Feature",
          geometry: polygonData,
          properties,
        },
      ];
    }
  } catch (error) {
    logger.error("Error processing geofence:", geofence?.id, error);
  }

  return [];
}

/**
 * Extracts a single Polygon geometry from various GeoJSON formats.
 * @param {Object} data - Parsed GeoJSON object
 * @returns {Object|null} Polygon geometry, or null if none found
 */
export function extractPolygonGeometry(data) {
  if (!data) return null;

  // Already a Polygon geometry
  if (data.type === "Polygon") {
    return data;
  }

  // FeatureCollection: use first Polygon feature's geometry
  if (data.type === "FeatureCollection") {
    const polygon = data.features.find(
      (feature) => feature.geometry?.type === "Polygon"
    );
    return polygon ? polygon.geometry : null;
  }

  // Feature: extract its geometry
  if (data.type === "Feature") {
    if (data.geometry && data.geometry.type === "Polygon") {
      return data.geometry;
    }
  }

  logger.error("[GeofenceContext] Unable to extract polygon geometry", {
    data,
  });
  return null;
}

/**
 * Computes a simple average-of-vertices centroid for a Polygon or MultiPolygon
 * geometry. Used for placing geofence centre markers/pins.
 * @param {{ type: string, coordinates: any[] }} geometry - GeoJSON geometry
 * @returns {[number, number]|null} `[lng, lat]` centroid, or null when unsupported
 */
export function getPolygonCentroid(geometry) {
  let coords;
  if (geometry?.type === "Polygon") {
    coords = geometry.coordinates[0];
  } else if (geometry?.type === "MultiPolygon") {
    coords = geometry.coordinates[0][0];
  } else {
    return null;
  }

  if (!coords?.length) return null;

  const x = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length;
  const y = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length;
  return [x, y];
}

/**
 * Computes a bounding box that encloses every supplied geofence polygon.
 * Handles FeatureCollection, Feature and Polygon `polygon_data` formats.
 * @param {Array<Object>} geofences - Geofence records containing polygon_data
 * @returns {[[number, number], [number, number]]|null} `[[minLng,minLat],[maxLng,maxLat]]` or null
 */
export function getGeofencesBounds(geofences) {
  if (!geofences || geofences.length === 0) return null;

  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  geofences.forEach((geofence) => {
    try {
      const polygonData = parsePolygonData(geofence.polygon_data);
      if (!polygonData) return;

      let coordinates = [];
      if (polygonData.type === "FeatureCollection") {
        polygonData.features.forEach((feature) => {
          if (feature.geometry.type === "Polygon") {
            coordinates.push(...feature.geometry.coordinates[0]);
          }
        });
      } else if (polygonData.type === "Feature") {
        if (polygonData.geometry.type === "Polygon") {
          coordinates = polygonData.geometry.coordinates[0];
        }
      } else if (polygonData.type === "Polygon") {
        coordinates = polygonData.coordinates[0];
      }

      coordinates.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });
    } catch (error) {
      logger.error("Error processing geofence bounds:", geofence.id, error);
    }
  });

  if (
    minLng === Infinity ||
    maxLng === -Infinity ||
    minLat === Infinity ||
    maxLat === -Infinity
  ) {
    return null;
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}
